// src/hooks/useUnsavedChangesGuard.js
import { useEffect } from 'react';

/**
 * Registers a `beforeunload` listener while `isDirty` is true.
 * Triggers the browser's native "Leave site?" confirmation when the user
 * tries to close the tab, refresh, or navigate away via the browser back/forward.
 *
 * Note: modern browsers (since ~2017) ignore custom message text in beforeunload
 * and show a generic browser-default prompt. We still call preventDefault and set
 * returnValue per the spec to ensure the dialog fires.
 *
 * Does NOT intercept in-app <Link> or programmatic navigate() calls — that path
 * is currently unguarded due to legacy <BrowserRouter> (no useBlocker available).
 * See DEF-038 for the in-app nav gap.
 *
 * @param {boolean} isDirty - When true, the guard is active.
 */
export function useUnsavedChangesGuard(isDirty) {
  useEffect(() => {
    if (!isDirty) return undefined;

    const handleBeforeUnload = (event) => {
      event.preventDefault();
      // Required for the dialog to fire in some browsers; text is ignored.
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);
}
